import {
  JSXElementConstructor,
  MouseEvent,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from 'react';
import {
  PublicTableContentProps,
  RenderRowDetail,
  TableCellProps,
  WbTableComponents,
} from '../types';
import { Table } from '@tanstack/react-table';

export interface WbTableContextProviderProps<T> {
  tanstackTableInstance: Table<T>;
  children?: ReactNode;
  columns?: Array<TableCellProps<T>>;
  components: WbTableComponents<T>;
  selection?: 'single' | 'multiple';
  selectionDisabled?: boolean;
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  checkIfDisabled?: (row: T) => boolean;
  tooltipIfDisabled?: string;
  treeProps?: PublicTableContentProps<T>['treeProps'];
  getRowId?: (row: T) => string;
  rowDynamicClassName?: (row: T) => string | undefined;
  onRowClick?: (
    row: T,
    event: MouseEvent<HTMLTableRowElement, MouseEvent>,
    index: number,
  ) => void;
  renderRowDetail?: RenderRowDetail<T>;
}

export interface WbTableContextType<T> extends WbTableContextProviderProps<T> {
  tanstackTableInstance: Table<T>;
  columns: Array<TableCellProps<T>>;
  disableRowHighlightOnHover: boolean;
  rows: Array<T>;
  RowsWrapper: JSXElementConstructor<any>;
  selection?: 'single' | 'multiple';
  selectionDisabled?: boolean;
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  checkIfDisabled?: (row: T) => boolean;
  tooltipIfDisabled?: string;
  renderRowDetail?: RenderRowDetail<T>;
}

export const WbTableContext = createContext<WbTableContextType<any> | null>(
  null,
);

export const DefaultWrapper = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

export const WbTableContextProvider = <T,>({
  tanstackTableInstance,
  children,
  components,
  selectedItems,
  setSelectedItems,
  selection,
  selectionDisabled,
  checkIfDisabled,
  tooltipIfDisabled,
  columns,
  renderRowDetail,
}: WbTableContextProviderProps<T>) => {
  const rows = components?.tableContent?.rows ?? [];
  const onRowClick = components.tableContent?.onRowClick;
  const rowDynamicClassName = components.tableContent?.rowDynamicClassName;
  const treeProps = components.tableContent?.treeProps;
  const getRowId = components.tableContent?.getRowId;
  // disable highlight on hover if specifief or no onRowClick
  const disableRowHighlightOnHover =
    components.tableContent?.disableRowHighlightOnHover ?? !onRowClick;

  const RowsWrapper = components?.tableContent?.rowsWrapper || DefaultWrapper;

  useEffect(() => {
    if (
      selection === 'single' &&
      rows.length > 0 &&
      (!selectedItems || selectedItems.length === 0)
    )
      setSelectedItems?.([rows[0]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WbTableContext.Provider
      value={{
        tanstackTableInstance,
        components,
        columns: columns ?? [],
        rows,
        selection,
        selectionDisabled,
        selectedItems,
        setSelectedItems,
        tooltipIfDisabled,
        checkIfDisabled,
        onRowClick,
        rowDynamicClassName,
        treeProps,
        getRowId,
        disableRowHighlightOnHover,
        RowsWrapper,
        renderRowDetail,
      }}
    >
      {children}
    </WbTableContext.Provider>
  );
};

export const useWbTableContext = <T,>() => {
  const context = useContext(WbTableContext);
  if (!context) {
    throw new Error(
      'useWbTableContext must be used within a WbTableContextProvider',
    );
  }
  return context as WbTableContextType<T>;
};
